// useFragments — learner-side live subscription to /fragments.
//
// Mirrors useMessages: dual query (broadcast + audienceUserIds CONTAINS me),
// merge by docId, filter retracted, sort createdAt DESC.
//
// Hook is kept separate from useMessages (not extracted to a generic) on
// purpose — generic over Firestore listener type narrowing collapses
// readability fast. Two near-identical hooks beats one inscrutable one.

import {
  type FragmentDoc,
  type WithId,
  fragmentsCol,
  useFirestoreCollection,
} from "@goodbye/firebase";
import { Timestamp, orderBy, query, where } from "firebase/firestore";
import { useMemo } from "react";

export interface FragmentsState {
  fragments: WithId<FragmentDoc>[];
  loading: boolean;
  error: Error | null;
}

function tsMs(t: Timestamp | null | undefined): number {
  if (!t || typeof t.toMillis !== "function") return 0;
  return t.toMillis();
}

export function useFragments(learnerUserId: string | null): FragmentsState {
  const broadcastQ = useMemo(() => {
    if (!learnerUserId) return null;
    return query(
      fragmentsCol(),
      where("broadcast", "==", true),
      orderBy("createdAt", "desc"),
    );
  }, [learnerUserId]);

  const targetedQ = useMemo(() => {
    if (!learnerUserId) return null;
    return query(
      fragmentsCol(),
      where("audienceUserIds", "array-contains", learnerUserId),
      orderBy("createdAt", "desc"),
    );
  }, [learnerUserId]);

  const broadcast = useFirestoreCollection(broadcastQ);
  const targeted = useFirestoreCollection(targetedQ);

  const merged = useMemo(() => {
    const byId = new Map<string, WithId<FragmentDoc>>();
    for (const f of broadcast.data) byId.set(f.id, f);
    for (const f of targeted.data) byId.set(f.id, f);
    const list = Array.from(byId.values()).filter(
      (f) => f.retracted !== true,
    );
    list.sort((a, b) => {
      const diff = tsMs(b.createdAt) - tsMs(a.createdAt);
      if (diff !== 0) return diff;
      return a.id.localeCompare(b.id);
    });
    return list;
  }, [broadcast.data, targeted.data]);

  if (!learnerUserId) {
    return { fragments: [], loading: false, error: null };
  }
  return {
    fragments: merged,
    loading: broadcast.loading || targeted.loading,
    error: broadcast.error ?? targeted.error,
  };
}
